import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Card,
  CardContent,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Pagination,
  Stack,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  QrCodeScanner as QrIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { flatsAPI } from '../services/api';
import SecurityGuardQRScanner from '../components/SecurityGuardQRScanner';

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getInitialDateRange = () => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(end.getDate() - 6);
  return { start: formatDateInput(start), end: formatDateInput(end) };
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`security-tabpanel-${index}`}
      aria-labelledby={`security-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 1, sm: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const SecurityGuardDashboard = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentTab, setCurrentTab] = useState(0);
  const [vehicleLogs, setVehicleLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const initialRange = getInitialDateRange();
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [page, setPage] = useState(1);
  const recordsPerPage = 10;

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Fetch vehicle entry logs
  const fetchVehicleLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = { startDate, endDate };
      
      const response = await flatsAPI.getVehicleEntryLogs(params);
      
      const logs = response.data.logs || [];
      
      setVehicleLogs(logs);
      
    } catch (error) {
      console.error('Error fetching vehicle logs:', error);
      setError('Failed to fetch vehicle logs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);



  useEffect(() => {
    if (currentTab === 1) { // History tab
      fetchVehicleLogs();
    }
  }, [currentTab, fetchVehicleLogs]);

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, vehicleLogs.length]);

  const pageCount = Math.max(1, Math.ceil(vehicleLogs.length / recordsPerPage));
  const paginatedLogs = vehicleLogs.slice((page - 1) * recordsPerPage, page * recordsPerPage);

  const handleStartDateChange = (value) => {
    const selectedStart = new Date(`${value}T00:00:00`);
    const selectedEnd = new Date(`${endDate}T00:00:00`);
    const earliestAllowed = new Date(selectedEnd);
    earliestAllowed.setMonth(earliestAllowed.getMonth() - 6);
    setStartDate(formatDateInput(selectedStart < earliestAllowed ? earliestAllowed : selectedStart));
  };

  const handleEndDateChange = (value) => {
    const selectedEnd = new Date(`${value}T00:00:00`);
    const selectedStart = new Date(`${startDate}T00:00:00`);
    const latestAllowed = new Date(selectedStart);
    latestAllowed.setMonth(latestAllowed.getMonth() + 6);
    const today = new Date();
    const cappedEnd = selectedEnd > today ? today : selectedEnd;
    setEndDate(formatDateInput(cappedEnd > latestAllowed ? latestAllowed : cappedEnd));
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 3 } }}>
      <Typography 
        variant="h4" 
        gutterBottom 
        align="center" 
        color="primary"
        sx={{
          fontSize: { xs: '1.5rem', md: '2rem' },
          mb: { xs: 2, md: 3 }
        }}
      >
        {t('securityGuard.dashboard.title', 'Security Guard Dashboard')}
      </Typography>

        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange} 
              aria-label="security guard tabs"
              variant={isMobile ? "fullWidth" : "standard"}
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab 
                label={t('securityGuard.dashboard.qrScanner', 'QR Scanner')} 
                icon={<QrIcon />} 
                iconPosition="start"
                sx={{ 
                  minHeight: { xs: 48, md: 72 },
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}
              />
              <Tab 
                label={t('securityGuard.dashboard.history', 'Entry History')} 
                icon={<HistoryIcon />} 
                iconPosition="start"
                sx={{ 
                  minHeight: { xs: 48, md: 72 },
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}
              />
            </Tabs>
          </Box>

          {/* QR Scanner Tab */}
          <TabPanel value={currentTab} index={0}>
            <SecurityGuardQRScanner />
          </TabPanel>

          {/* Entry History Tab */}
          <TabPanel value={currentTab} index={1}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <TextField
                type="date"
                label="From date"
                value={startDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
                fullWidth={isMobile}
                size={isMobile ? 'small' : 'medium'}
                sx={{ maxWidth: { xs: '100%', sm: 300 } }}
                inputProps={{ max: endDate }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                type="date"
                label="To date"
                value={endDate}
                onChange={(e) => handleEndDateChange(e.target.value)}
                fullWidth={isMobile}
                size={isMobile ? 'small' : 'medium'}
                sx={{ maxWidth: { xs: '100%', sm: 300 } }}
                inputProps={{ min: startDate, max: formatDateInput(new Date()) }}
                InputLabelProps={{ shrink: true }}
              />
              <Typography variant="body2" color="text.secondary">
                {vehicleLogs.length} record(s). Maximum range: six months.
              </Typography>
            </Stack>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {vehicleLogs.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {loading
                        ? t('common.loading', 'Loading...')
                        : t('securityGuard.dashboard.noLogs', 'No vehicle logs found for the selected filters')}
                    </Typography>
                  </Paper>
                ) : (
                  paginatedLogs.map((log, index) => (
                    <Card key={log.logId || index} variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                              {log.vehicleNumber || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(log.timestamp).toLocaleString()}
                            </Typography>
                          </Box>
                          <Chip
                            label={log.action}
                            size="small"
                            color={log.action === 'ENTRY' ? 'success' : 'warning'}
                            sx={{ flexShrink: 0, fontWeight: 700 }}
                          />
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', columnGap: 1.5, rowGap: 0.75 }}>
                          <Typography variant="body2" color="text.secondary">Flat</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>{log.flatNumber || '-'}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('securityGuard.dashboard.timestamp', 'Timestamp')}</TableCell>
                    <TableCell>{t('securityGuard.dashboard.vehicleNumber', 'Vehicle Number')}</TableCell>
                    <TableCell>{t('securityGuard.dashboard.action', 'Action')}</TableCell>
                    <TableCell>{t('securityGuard.dashboard.flatNumber', 'Flat')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vehicleLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="textSecondary" py={4}>
                          {loading 
                            ? t('common.loading', 'Loading...') 
                            : t('securityGuard.dashboard.noLogs', 'No vehicle logs found for the selected filters')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedLogs.map((log, index) => (
                      <TableRow key={log.logId || index}>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(log.timestamp).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2">
                            {log.vehicleNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.action}
                            size="small"
                            color={log.action === 'ENTRY' ? 'success' : 'warning'}
                          />
                        </TableCell>
                        <TableCell>{log.flatNumber || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
                </Table>
              </TableContainer>
            )}
            {!loading && vehicleLogs.length > recordsPerPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination count={pageCount} page={page} onChange={(_, value) => setPage(value)} color="primary" />
              </Box>
            )}
          </TabPanel>


        </Box>
      </Container>
  );
};

export default SecurityGuardDashboard;
